import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress,
  Divider,
  Chip,
} from '@mui/material';
import { useAppSelector } from '../../store/hooks';

interface ViewColumnModalProps {
  open: boolean;
  onClose: () => void;
}

const ViewColumnModal: React.FC<ViewColumnModalProps> = ({ open, onClose }) => {
  const { selectedColumn, loading } = useAppSelector((state) => state.columns);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Detalle de la Columna</DialogTitle>
      <DialogContent>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <CircularProgress />
          </Box>
        ) : selectedColumn ? (
          <Box sx={{ mt: 2 }}>
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" color="text.secondary">
                Nombre de la Columna
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                {selectedColumn.columnName}
              </Typography>
              <Divider />
            </Box>

            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" color="text.secondary">
                Tipo de Dato
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                {selectedColumn.dataType}
              </Typography>
              <Divider />
            </Box>

            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" color="text.secondary">
                Descripción
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                {selectedColumn.description}
              </Typography>
              <Divider />
            </Box>

            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" color="text.secondary">
                Reglas de Negocio
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                {selectedColumn.businessRules}
              </Typography>
              <Divider />
            </Box>

            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" color="text.secondary">
                Valores de Ejemplo
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                {selectedColumn.exampleValues}
              </Typography>
              <Divider />
            </Box>

            <Box sx={{ mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Nullable
                </Typography>
                <Chip
                  label={selectedColumn.isNullable ? 'Sí' : 'No'}
                  color={selectedColumn.isNullable ? 'warning' : 'default'}
                  size="small"
                />
              </Box>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Llave Primaria
                </Typography>
                <Chip
                  label={selectedColumn.isPrimaryKey ? 'Sí' : 'No'}
                  color={selectedColumn.isPrimaryKey ? 'primary' : 'default'}
                  size="small"
                />
              </Box>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Llave Foránea
                </Typography>
                <Chip
                  label={selectedColumn.isForeignKey ? 'Sí' : 'No'}
                  color={selectedColumn.isForeignKey ? 'secondary' : 'default'}
                  size="small"
                />
              </Box>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Es Core
                </Typography>
                <Chip
                  label={selectedColumn.isCore ? 'Sí' : 'No'}
                  color={selectedColumn.isCore ? 'success' : 'default'}
                  size="small"
                />
              </Box>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Siempre Incluir
                </Typography>
                <Chip
                  label={selectedColumn.alwaysInclude ? 'Sí' : 'No'}
                  color={selectedColumn.alwaysInclude ? 'info' : 'default'}
                  size="small"
                />
              </Box>
            </Box>
            <Divider sx={{ mb: 2 }} />

            {selectedColumn.isForeignKey && selectedColumn.foreignKeyReference && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Referencia de Llave Foránea
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {selectedColumn.foreignKeyReference}
                </Typography>
                <Divider />
              </Box>
            )}

            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" color="text.secondary">
                Palabras Clave
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                {selectedColumn.keywords.join(', ')}
              </Typography>
              <Divider />
            </Box>

            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" color="text.secondary">
                Valores Permitidos
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                {selectedColumn.allowedValues?.join(', ') || '-'}
              </Typography>
              <Divider />
            </Box>

            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" color="text.secondary">
                Fecha de Creación
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                {selectedColumn.createdAt}
              </Typography>
              <Divider />
            </Box>

            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" color="text.secondary">
                Fecha de Actualización
              </Typography>
              <Typography variant="body1">
                {selectedColumn.updatedAt}
              </Typography>
            </Box>
          </Box>
        ) : (
          <Typography>No hay columna seleccionada</Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="outlined">
          Cerrar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ViewColumnModal;
